// src/utils/useIframeAutoHeight.js
import { useEffect, useRef } from 'react';

function debounce(fn, wait = 100) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), wait);
  };
}

export default function useIframeAutoHeight() {
  const send = useRef(null);

  useEffect(() => {
    const post = (h) => {
      try {
        window.parent?.postMessage({ frameHeight: h }, '*');
      } catch {}
    };

    const measure = () => {
      // take the max to account for different layout roots
      const body = document.body;
      const html = document.documentElement;
      const root = document.getElementById('root');
      const heights = [
        body?.scrollHeight || 0,
        html?.scrollHeight || 0,
        root?.scrollHeight || 0,
        body?.offsetHeight || 0,
        html?.offsetHeight || 0,
      ];
      return Math.max(...heights);
    };

    const sendNow = () => post(measure());
    const sendDebounced = debounce(sendNow, 120);
    send.current = sendDebounced;

    // 1) initial after first paint
    requestAnimationFrame(sendNow);
    // 2) again after window load (images/styles done)
    window.addEventListener('load', sendNow, { once: true });
    // 3) on resize
    window.addEventListener('resize', sendDebounced);

    // 4) respond to DOM size/content mutations
    const ro = new ResizeObserver(() => sendDebounced());
    ro.observe(document.body);

    // 5) as a backstop, observe DOM mutations (new tiles/images)
    const mo = new MutationObserver(() => sendDebounced());
    mo.observe(document.body, { childList: true, subtree: true });

    // 6) periodically ping while user scrolls (optional but helpful)
    const onScroll = debounce(sendNow, 150);
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.removeEventListener('resize', sendDebounced);
      window.removeEventListener('scroll', onScroll);
      ro.disconnect();
      mo.disconnect();
    };
  }, []);
}
